import React, { useContext, useCallback, Fragment } from 'react'
import { t } from '@insight/toolkit-utils'
import { Pagination, Message, Icon, Button, connectToLocale } from '@insight/toolkit-react'

import SearchControls from '../SearchControls/SearchControls'
import CoveoFacets from '../SearchFilter/CoveoFacets'
import SearchProducts from '../SearchProducts/SearchProducts'

import { SearchContext } from '../../context/SearchContext'
import { hideHeroImageBanner } from '../../shared/hideHeroImage'
import { NoResultsFoundWithFacets } from '../SearchNotFound/NoResultsFoundWithFacets'
import SearchNotFound from '../SearchNotFound/SearchNotFound'
import {
  splitFacets, containsSpaceOrDoubleQuote, escapeDoubleQuotes,
  containsSpecialCharacterForFacets
} from '../../shared/utils'
import {
  hasCookie,
  getCookie,
  deleteCookie,
} from '@insight/toolkit-utils/lib/helpers/cookieHelpers'
import {
  IPS_PAGE_URL_COOKIE_NAME,
  IPS_CONTRACT_ID_COOKIE_NAME,
  IPS_CONTRACT_NAME_COOKIE_NAME,
  ADD_TO_CART_CATEGORY_TYPES,
  INSTOCK_ONLY_KEY,
} from '../../constants'

export const ProductsTab = ({
  landingPageInfo,
  tabType,
  showFilters,
  setShowFilters,
  context
}) => {
  const {
    currency,
    contents,
    salesOrg,
    originalSpelling,
    correctedSpelling,
    instockOnly,
    products,
    searchSSEError,
    numFound: totalRecords,
    start,
    view,
    pages: totalPages,
    pageNumber: currentPage,
    facetGenerator,
    isSearching,
    firstSearchExecuted,
    error,
    searchProducts,
    originalFilters,
    selectedFilters,
    setFilter,
    clearFilters,
    origParams,
    outOfStockOnly,
    paginationController,
    searchHandler: coveoSearchHandler,
  } = useContext(SearchContext)
  const { contract, isLoggedIn, isIPSUser } = context;
  const isLogoutIPSUser = isIPSUser && !isLoggedIn
  const IPSLogoutContractName = isLogoutIPSUser ? contract?.contractName || 'OPEN MARKET' : null
  const IPSLogoutTabName = contract?.contractName ? t('Change contract') : t('Select a contract')
  const isSingleContract = isIPSUser && (isLoggedIn && contract?.contractName !== "All" || !isLoggedIn && !!contract)
  const ipsPageUrlCookie = hasCookie(IPS_PAGE_URL_COOKIE_NAME) ? getCookie(IPS_PAGE_URL_COOKIE_NAME) : '/en_US/shop/contracts.html'
  const { isBrandPage, isLandingPage, initQuery } = landingPageInfo
  const hasCategoryFilterApplied = !!origParams?.category
  const hasFacetFilterApplied = !!origParams?.selectedFacet
  const hasRangeFilterApplied = !!origParams?.rangeFacet
  const showProductNoResultPage = !(
    hasCategoryFilterApplied ||
    hasFacetFilterApplied ||
    hasRangeFilterApplied
  )
  const removeContract = () => {
    deleteCookie(IPS_PAGE_URL_COOKIE_NAME)
    deleteCookie(IPS_CONTRACT_ID_COOKIE_NAME)
    deleteCookie(IPS_CONTRACT_NAME_COOKIE_NAME)
    window.location.reload()
  }
  const isLogoutOpenMarket =
    isLogoutIPSUser && IPSLogoutContractName === 'OPEN MARKET'
  const IPSLogoutTabs = isLogoutIPSUser ? (
    <div className="c-search-ips-tab">
      <div className="o-grid o-grid--center">
        <div className="o-grid__item">{IPSLogoutContractName}</div>
        {!isLogoutOpenMarket && (
          <div className="o-grid__item--shrink c-search-ips-tab__remove-contract">
            <Button onClick={removeContract} aria-label={t('Remove contract')}>
              <Icon
                color="primary"
                icon="close"
                title="Remove contract"
                type="alert"
              />
            </Button>
          </div>
        )}
      </div>
      <Button
        className="c-cart-actions__contract-change"
        color="link"
        href={ipsPageUrlCookie}
        size="small"
      >
        {IPSLogoutTabName}
      </Button>
    </div>
  ) : null
  const queryString = window.location.search
  const params = new URLSearchParams(queryString)

  // must be a positive finite integer, else default to 1
  const safeTotalPages = Number.isFinite(Number(totalPages)) && Number(totalPages) > 0
    ? Math.floor(Number(totalPages))
    : 1

  // must be a finite integer, between 1 and totalPages
  const safeCurrentPageRaw = Number.isFinite(Number(currentPage))
    ? Math.floor(Number(currentPage))
    : 1
  const safeCurrentPage = Math.min(Math.max(safeCurrentPageRaw, 1), safeTotalPages)

  const query = correctedSpelling ? correctedSpelling : params.get('q')
  // in case of Brand or Category search pages, there is a chance of having search term from legacy page query
  let queryToDisplay = query || origParams?.q

  if (!queryToDisplay && initQuery) {
    try {
      queryToDisplay = new URL(initQuery).searchParams.get('q') || initQuery
    } catch {
      queryToDisplay = initQuery
    }
  }

  // check for wildcard search keyword
  if (queryToDisplay === '*:*') {
    queryToDisplay = t('All products')
  }

  const toggleFilters = () => {
    setShowFilters(!showFilters)
  }

  const onHandleClearAll = () => {
    if ('URLSearchParams' in window && Object.keys(origParams).length > 0) {
      const searchParams = new URLSearchParams()
      Object.keys(origParams).map((param) => {
        if (['selectedFacet', 'rangeFacet'].includes(param)) {
          searchParams.delete(param)
        } else {
          searchParams.set(param, origParams[param])
        }
      })
      searchProducts(searchParams.toString(), contents, salesOrg, contract)
    }
    // clearFilters() -> activeFacetGenerator.deselectAll() already clears the
    // in-stock facet along with every other facet, so no separate in-stock
    // handler call is needed here.
    clearFilters()
  }

  const onPageChange = (page) => {
    // Coveo pagination is 0-indexed; the toolkit Pagination component is 1-indexed
    paginationController.selectPage(page - 1)
    window.scroll({
      top: 0,
      left: 0,
      behavior: 'smooth',
    })
  }

  const onInStockFilterChange = () => {
    // Toggle the IN_STOCK_STATUS_FIELD facet through the Coveo search handler
    coveoSearchHandler({ key: INSTOCK_ONLY_KEY, value: !instockOnly })
  }

  const onCategoryFilterChange = (category, clearGroup = false) => {
    const value = clearGroup ? '' : category.val
    searchHandler({ key: 'category', value })
  }

  const handleFilterChange = (filterType, option, clearGroup = false) => {
    const optionValue = option.val;
    // Note: LW expects quotes for regular facets with spaces and quotes to be wrapped in quotes, whereas same do not apply for Range facets
    // check string whether space and quote exists or contains special characters
    // if yes then wrap with quote
    // if no then return as it is
    const wrappedOptionValue =
      filterType === 'selectedFacet'
        && (containsSpaceOrDoubleQuote(optionValue) || containsSpecialCharacterForFacets(optionValue))
        ? `"${escapeDoubleQuotes(optionValue)}"`
        : optionValue
    const currentSelectedFacets = origParams[filterType]
    if (currentSelectedFacets) {
      const selectedFacetsMaps = {}
      // process this into map to easy manipulation
      const optionGroups = Array.isArray(currentSelectedFacets)
        ? currentSelectedFacets
        : splitFacets(currentSelectedFacets)
      optionGroups.map((optionGroup) => {
        const [groupName, values] = optionGroup.split(':')
        selectedFacetsMaps[groupName] = values.split('|')
      })
      // if clear group is enabled, we just need to clear the group
      if (option.group in selectedFacetsMaps && !clearGroup) {
        // found in params, then identify if value exists, if yes remove else add
        const valuesInGroup = selectedFacetsMaps[option.group]
        if (valuesInGroup.includes(wrappedOptionValue.toString())) {
          // remove
          selectedFacetsMaps[option.group] = valuesInGroup.filter(
            (val) => val != wrappedOptionValue
          )
        } else {
          // add
          selectedFacetsMaps[option.group] = [
            ...valuesInGroup,
            wrappedOptionValue,
          ]
        }
      } else {
        // not enter new key and value array
        selectedFacetsMaps[option.group] = [wrappedOptionValue]
      }

      // construct selectedFacets value
      let SelectedFacetsString = ''
      Object.keys(selectedFacetsMaps).map((groupName, index) => {
        if (selectedFacetsMaps[groupName].length > 0) {
          SelectedFacetsString += `${!!SelectedFacetsString ? ',' : ''
            }${groupName}:${selectedFacetsMaps[groupName].join('|')}`
        }
      })
      searchHandler({ key: filterType, value: SelectedFacetsString })
    } else {
      // has no selected facets add new one
      searchHandler({
        key: filterType,
        value: `${option.group}:${wrappedOptionValue}`,
      })
    }
    setFilter(option, clearGroup)
  }

  const onRangeFilterChange = (range, clearGroup) => {
    handleFilterChange('rangeFacet', range, clearGroup)
  }

  // Commenting for now to compare how it was used in LucidWorks before
  // const OnFilterChange = (option, clearGroup) => {
  //   handleFilterChange('selectedFacet', option, clearGroup)
  // }

  const searchHandler = ({ key, value }) => {
    // Delegate sort and page-size changes directly to the Coveo controllers
    if (['rows', 'customSort'].includes(key)) {
      coveoSearchHandler({ key, value })
      return
    }
    // instockOnly is now managed as a Coveo facet, not a URL parameter
    if (key === INSTOCK_ONLY_KEY) {
      coveoSearchHandler({ key, value })
      return
    }
    // diff with searchProducts
    // add origParams and change as params and call searchProducts
    if ('URLSearchParams' in window && Object.keys(origParams).length > 0) {
      const searchParams = new URLSearchParams()
      Object.keys(origParams).map((param) => {
        if (
          ['selectedFacet', 'rangeFacet'].includes(param) &&
          Array.isArray(origParams[param])
        ) {
          searchParams.set(param, origParams[param].join(','))
        } else {
          //We don't need to include the params (userSegment, userId, sessionId) as it is already included
          //in the getData.js axios GET url param. Otherwise, we risk duplicating URL parameters as raised in CES-1640
          if (
            ![
              'userSegment',
              'userId',
              'sessionId',
              'soldTo',
              'webGroup',
            ].includes(param)
          ) {
            searchParams.set(param, origParams[param])
          }
        }
      })
      searchParams.set(key, value)
      // reset page number when new request is not related to page and number of records
      // but we can still manually set start as 0 when needed
      if (!['start'].includes(key)) {
        searchParams.set('start', '0')
      }
      if (!value) {
        searchParams.delete(key)
      }
      searchProducts(searchParams.toString(), contents, salesOrg, contract);
    }
  }

  if (origParams?.searchType === 'category') {
    hideHeroImageBanner()
  }

  const handleProductOnClick = useCallback((product, productUrl, position) => {
    let analyticsListName = ADD_TO_CART_CATEGORY_TYPES.MAIN_SEARCH_RESULTS
    if (isBrandPage) {
      analyticsListName = 'Shop Partner'
    } else if (isLandingPage) {
      analyticsListName = 'Shop Category'
    }
  },[isBrandPage, isLandingPage])

  const hasRequestCompleted = firstSearchExecuted || !!error
  return hasRequestCompleted && showProductNoResultPage && !isSearching && !products?.length ? (<Fragment>
        {error &&
            <Message
                type='warning'
                children={t('It looks like we’ve encountered a technical issue. Please try your search again. The issue has been reported to our technical team.')}
                ariaLive='polite'
            />
        }
        <SearchNotFound query={queryToDisplay} isProductsTab={true} />
      </Fragment>

  ) : (
    <>
      {hasCategoryFilterApplied && (
        <div className="c-search-page__clear-category">
          <Button
            className="u-text-bold"
            color="inline-link"
            onClick={() => onCategoryFilterChange({}, true)}
          >
            {t('Search all categories instead')}
          </Button>
        </div>
      )}
      {(error || searchSSEError) &&
          <Message
            type='warning'
            children={t('It looks like we’ve encountered a technical issue. Please try your search again. The issue has been reported to our technical team.')}
            ariaLive='polite'
          />
      }
      <div className="o-grid">
        <div className="c-search-page__filters-container o-grid__item u-1/1 u-1/4@desktop">
          {IPSLogoutTabs}
          <CoveoFacets
            facetGenerator={facetGenerator}
            showFilters={showFilters}
            toggleFilters={toggleFilters}
            instockOnly={instockOnly}
            outOfStockOnly={outOfStockOnly}
            setStockFilter={onInStockFilterChange}
          />
        </div>
        <div className="c-search-page__products-container o-grid__item u-1/1 u-3/4@desktop">
          <SearchControls
            clearFilters={onHandleClearAll}
            correctedSpelling={correctedSpelling}
            currentPage={currentPage}
            originalSpelling={originalSpelling}
            query={queryToDisplay}
            originalFilters={originalFilters}
            searchHandler={searchHandler}
            setRange={onRangeFilterChange}
            setStockFilter={onInStockFilterChange}
            start={start}
            toggleFilters={toggleFilters}
            totalRecords={totalRecords}
          />
          {(firstSearchExecuted && !isSearching && !products?.length) ? (
            <NoResultsFoundWithFacets clearFilters={onHandleClearAll} />
          ) : (
            <SearchProducts
              currency={currency}
              view={view}
              products={products}
              loading={isSearching}
              onHandleProductOnClick={handleProductOnClick}
              currentPage={currentPage}
            />
          )}
        </div>
        <Pagination
          currentPage={safeCurrentPage}
          totalPages={safeTotalPages}
          pageHandler={onPageChange}
        />
      </div>
    </>
  )
}

export default connectToLocale(ProductsTab)
